import React from 'react';
import { CalendarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { EventDto } from './types';
import { resolveThemePreset } from './themePresets';
import { formatEventWindow } from './formatEventWindow';
import { EventBody } from './EventBody';
import { resolveEventSurface } from './surfaceContent';
import type { EventImageUrlResolver } from './EventBody';

export interface EventMeetingModalContentProps {
    readonly event: EventDto;
    readonly resolveImageUrls?: EventImageUrlResolver;
}

// The pop-up of a `kind: 'meeting'` event: date/time hero, authored body and
// the add-to-calendar block.
export const EventMeetingModalContent: React.FC<EventMeetingModalContentProps> = ({
    event,
    resolveImageUrls
}) => {
    const theme = resolveThemePreset(event.themePreset);
    const content = resolveEventSurface(event, 'modal');
    const dateTimeLine = formatEventWindow(event.startsAt, event.endsAt);

    return (
        <div className="space-y-6">
            <div className={`${theme.bg} rounded-xl p-5`}>
                <h3 className="text-base font-bold text-gray-900 mb-1">{content.title}</h3>
                {/* The separator belongs to the topic: a topic-less event would otherwise
                    render a dash dangling off the time. */}
                <p className="text-sm text-gray-700 leading-relaxed">
                    {dateTimeLine}
                    {event.topic && ` · ${event.topic}`}
                </p>
            </div>

            <EventBody
                bodyDoc={event.bodyDoc}
                body={event.body}
                resolveImageUrls={resolveImageUrls}
            />

            {(event.googleCalendarUrl || event.icsUrl) && (
                <div className="space-y-1.5">
                    <h4 className="text-sm font-bold text-gray-900">Add to your calendar</h4>
                    <div className="flex gap-2">
                        {event.googleCalendarUrl && (
                            <a
                                href={event.googleCalendarUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-2 bg-black text-white font-semibold px-4 py-1.5 rounded-lg shadow hover:bg-gray-900 transition-colors text-sm"
                            >
                                <CalendarIcon className="h-4 w-4" aria-hidden="true" />
                                Google Calendar
                            </a>
                        )}
                        {event.icsUrl && (
                            <a
                                href={event.icsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 flex items-center justify-center gap-2 bg-white text-gray-800 border border-gray-300 font-semibold px-4 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            >
                                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                                Download .ics
                            </a>
                        )}
                    </div>
                </div>
            )}

            <div className="text-sm text-gray-700 border-t border-gray-100 pt-4 space-y-1">
                <p>We hope you can join us.</p>
                <p className="text-gray-500">Best,</p>
                <p className="text-gray-500">The ODO Team</p>
            </div>
        </div>
    );
};

export default EventMeetingModalContent;
